import { styled } from "goober";
import { useState } from "react";
import { CHAT_WORKFLOWS_FLAG } from "../../../shared/featureFlagNames";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";
import { mobileMedia, useIsMobile } from "../../hooks/useIsMobile";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { ChatSuggestions } from "./ChatSuggestions";

const Overlay = styled("div")`
  position: absolute;
  inset: 0;
  display: flex;
  padding: 24px;
  box-sizing: border-box;
  pointer-events: none;
  overflow-y: auto;
  @media (max-height: 800px) {
    padding: 16px 24px;
  }
  ${mobileMedia} {
    padding: 16px;
  }
`;

const Inner = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  width: 100%;
  max-width: 600px;
  /* centers like align/justify center, but degrades to scrolling instead of
     clipping the top when the content is taller than a small viewport */
  margin: auto;
  ${mobileMedia} {
    gap: 16px;
  }
  @media (max-height: 800px) {
    gap: 16px;
  }
  pointer-events: auto;
  transition:
    opacity var(--motion-fast) var(--ease-out),
    transform var(--motion-fast) var(--ease-out);
  &[data-visible="false"] {
    opacity: 0;
    transform: translateY(-4px);
    pointer-events: none;
  }
`;

const Header = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  text-align: center;

  ${mobileMedia} {
    gap: 4px;
  }
`;

type Props = {
  onPick: (prompt: string) => void;
  iconColor: string;
  userName: string;
  seed: string;
  visible: boolean;
  content?: {
    title: string;
    description: string;
    prompts: readonly string[];
  };
};

export function greetingForHour(
  hour: number
): "Morning" | "Afternoon" | "Evening" {
  if (hour < 12) return "Morning";
  if (hour < 18) return "Afternoon";
  return "Evening";
}

// A user with no display name has `name` defaulted to their email, so an
// email-looking name means there is no real name to greet them by.
export function greetingName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed || trimmed.includes("@")) return "";
  return trimmed.split(" ")[0] ?? "";
}

export function NewChatEmptyState({
  onPick,
  iconColor,
  userName,
  seed,
  visible,
  content,
}: Props) {
  const isMobile = useIsMobile();
  const chatWorkflows = useFeatureFlag(CHAT_WORKFLOWS_FLAG);

  const [greeting] = useState(() => greetingForHour(new Date().getHours()));
  const title = userName
    ? `${greeting}, ${userName}`
    : `Good ${greeting.toLowerCase()}`;

  return (
    <Overlay>
      <Inner data-visible={visible} inert={!visible}>
        <Header>
          <Icon
            name="ask"
            size={isMobile ? 24 : 36}
            color={iconColor}
            style={isMobile ? undefined : { transform: "translateY(-8px)" }}
          />
          <Text
            variant="display-md"
            color="var(--text-primary)"
            style={isMobile ? { fontSize: 14, lineHeight: "20px" } : undefined}
          >
            {content?.title ?? title}
          </Text>
          <Text
            variant="text-regular"
            color="var(--text-secondary)"
            style={{ maxWidth: 360 }}
          >
            {content?.description ??
              (chatWorkflows
                ? "Get work done, build workflows and apps, and explore your connectors and data."
                : "Chat is your lightweight personal space for quick tasks and questions — plugged into all your connectors.")}
          </Text>
        </Header>
        <ChatSuggestions
          onPick={onPick}
          seed={seed}
          prompts={content?.prompts}
        />
      </Inner>
    </Overlay>
  );
}
